import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { useData } from "../lib/DataContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, ChevronRight, CheckSquare, Clock, Calendar as CalIcon, GripVertical } from "lucide-react";
import { toast } from "sonner";

const PRIO = { Critical: "#DC2626", High: "#EA580C", Medium: "#2563EB", Low: "#64748B" };
const TYPE = { task: "#2563EB", followup: "#CA8A04", meeting: "#7C3AED" };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}
function weekDays(ref) {
  const start = new Date(ref); start.setDate(ref.getDate() - ref.getDay());
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}

export default function Calendar() {
  const { projects, departments, users } = useData();
  const [cur, setCur] = useState(new Date());
  const [view, setView] = useState("month");
  const [events, setEvents] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [filters, setFilters] = useState({ project_id: "all", department_id: "all", owner_id: "all" });

  const year = cur.getFullYear(), month = cur.getMonth();
  const days = view === "month" ? monthMatrix(year, month) : weekDays(cur);
  const todayIso = iso(new Date());

  const load = useCallback(() => {
    const start = iso(days[0]), end = iso(days[days.length - 1]);
    const params = { start, end };
    if (filters.project_id !== "all") params.project_id = filters.project_id;
    if (filters.department_id !== "all") params.department_id = filters.department_id;
    if (filters.owner_id !== "all") params.owner_id = filters.owner_id;
    api.get("/calendar", { params }).then((r) => setEvents(r.data));
    // eslint-disable-next-line
  }, [days[0]?.getTime(), days[days.length - 1]?.getTime(), filters]);
  useEffect(() => { load(); }, [load]);

  const byDate = {};
  events.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
  const label = view === "month"
    ? cur.toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : `${days[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;

  const nav = (dir) => {
    if (view === "month") setCur(new Date(year, month + dir, 1));
    else { const d = new Date(cur); d.setDate(cur.getDate() + dir * 7); setCur(d); }
  };

  const onDrop = async (dateKey, e) => {
    e.preventDefault(); setDragOver(null);
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    const { id, date } = JSON.parse(raw);
    if (date === dateKey) return;
    try {
      await api.post(`/tasks/${id}/reschedule`, { current_due_date: dateKey, source: "calendar" });
      toast.success("Due date updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not reschedule"); }
  };

  const chip = (e, j) => {
    const color = e.type === "task" ? PRIO[e.priority] : TYPE[e.type];
    return (
      <button key={j}
        draggable={e.type === "task"}
        onDragStart={e.type === "task" ? (ev) => ev.dataTransfer.setData("text/plain", JSON.stringify({ id: e.id, date: e.date })) : undefined}
        onClick={() => e.type !== "meeting" && setOpenId(e.id)}
        data-testid={`cal-event-${e.human_id || e.id}`}
        className="group w-full text-left text-[10px] rounded px-1 py-0.5 truncate flex items-center gap-1 cursor-pointer"
        style={{ background: color + "1a", color }}>
        {e.type === "task" && <GripVertical size={9} className="opacity-0 group-hover:opacity-60 shrink-0" />}
        {e.type === "task" ? <CheckSquare size={9} className="shrink-0" /> : e.type === "followup" ? <Clock size={9} className="shrink-0" /> : <CalIcon size={9} className="shrink-0" />}
        <span className="truncate">{e.title}</span>
      </button>
    );
  };

  return (
    <div className="p-6">
      <PageHeader title="Calendar" subtitle="Drag a task to another day to reschedule it">
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border overflow-hidden mr-1">
            <button onClick={() => setView("month")} data-testid="cal-view-month" className={`px-3 py-1.5 text-xs font-medium ${view === "month" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>Month</button>
            <button onClick={() => setView("week")} data-testid="cal-view-week" className={`px-3 py-1.5 text-xs font-medium ${view === "week" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>Week</button>
          </div>
          <Button variant="outline" size="sm" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
          <span className="text-sm font-semibold w-48 text-center" data-testid="cal-label">{label}</span>
          <Button variant="outline" size="sm" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight size={16} /></Button>
          <Button variant="outline" size="sm" onClick={() => setCur(new Date())}>Today</Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-3">
        <Select value={filters.project_id} onValueChange={(v) => setFilters((f) => ({ ...f, project_id: v }))}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="cal-filter-project"><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.department_id} onValueChange={(v) => setFilters((f) => ({ ...f, department_id: v }))}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="cal-filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Departments</SelectItem>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.owner_id} onValueChange={(v) => setFilters((f) => ({ ...f, owner_id: v }))}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="cal-filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Owners</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-3 text-xs text-slate-500 ml-auto">
          {Object.entries(TYPE).map(([k, c]) => (
            <span key={k} className="flex items-center gap-1.5 capitalize"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{k}</span>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 border-b bg-slate-50 text-xs font-semibold text-slate-500 uppercase">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const key = iso(d);
            const inMonth = view === "week" || d.getMonth() === month;
            const evs = byDate[key] || [];
            const showMax = view === "week" ? 12 : 3;
            return (
              <div key={i} data-testid={`cal-day-${key}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                onDragLeave={() => setDragOver((k) => (k === key ? null : k))}
                onDrop={(e) => onDrop(key, e)}
                className={`${view === "week" ? "min-h-[420px]" : "min-h-[104px]"} border-b border-r p-1.5 transition-colors ${inMonth ? "bg-white" : "bg-slate-50/50"} ${key === todayIso ? "ring-2 ring-inset ring-blue-400" : ""} ${dragOver === key ? "bg-blue-50" : ""}`}>
                <div className={`text-xs mb-1 ${inMonth ? "text-slate-600" : "text-slate-300"} ${key === todayIso ? "font-bold text-blue-600" : ""}`}>{d.getDate()}</div>
                <div className="space-y-1">
                  {evs.slice(0, showMax).map(chip)}
                  {evs.length > showMax && <div className="text-[9px] text-slate-400 pl-1">+{evs.length - showMax} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
